import React, { useState, useEffect, useRef } from 'react';
import API from '../api';
import { toast } from 'react-hot-toast';
import { useTranslation } from '../hooks/useTranslation';
import { queryTransaction } from '../services/GenerateQrAPI';
import ThermalReceipt from './ThermalReceipt';

// First Topup Policy Constants
const FIRST_TOPUP_POLICY = {
  mifare: {
    totalAmount: 400000,    // Total payment required for first topup
    deposit: 100000,         // Amount kept as deposit
    balance: 300000,         // Actual balance added to card
    name: 'Mifare Card'
  },
  obu: {
    totalAmount: 1200000,   // Total payment required for first topup
    deposit: 200000,         // Amount kept as deposit
    balance: 1000000,        // Actual balance added to device
    name: 'OBU Device'
  }
};

export default function QRTopup() {
  const { t, translate } = useTranslation();
  const [formData, setFormData] = useState({
    cardUid: '',
    cardType: '', // 'mifare' or 'obu'
    amount: '',
    description: '',
    paymentMethod: 'cash', // cash or laoqr
    selectedBank: 'LDB' // Only LDB supported
  });
  const [qrImage, setQrImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [transactionData, setTransactionData] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [statusCheckInterval, setStatusCheckInterval] = useState(null);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const searchRef = useRef(null);

  // New state for license plate search
  const [searchMode, setSearchMode] = useState('plate'); // 'plate' or 'device' - Default to plate
  const [plateSearchQuery, setPlateSearchQuery] = useState('');
  const [plateSearchResults, setPlateSearchResults] = useState([]);
  const [showPlateSuggestions, setShowPlateSuggestions] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const plateSearchRef = useRef(null);
  const qrDisplayRef = useRef(null);
  const [showReceipt, setShowReceipt] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  const [printWidth, setPrintWidth] = useState('72mm');

  // First topup policy state
  const [isFirstTopup, setIsFirstTopup] = useState(false);

  // Cleanup status check interval on unmount
  useEffect(() => {
    return () => {
      if (statusCheckInterval) {
        clearInterval(statusCheckInterval);
      }
    };
  }, [statusCheckInterval]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
      if (plateSearchRef.current && !plateSearchRef.current.contains(event.target)) {
        setShowPlateSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Search for cards and devices
  const searchCardsAndDevices = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      setShowSuggestions(false);
      return;
    }

    try {
      const response = await API.get('/wallet/cards-devices/search', {
        params: { query },
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setSearchResults(response.data || []);
      setShowSuggestions(true);
    } catch (error) {
      // If backend returns 404 for some queries (e.g., certain OBU ids), treat as "no results"
      if (error.response && error.response.status === 404) {
        console.debug('No cards/devices found for query (404).');
        setSearchResults([]);
        setShowSuggestions(false);
        // Optionally show a subtle info toast so the user knows nothing matched
        // toast.info(t('topup.no_devices_found'));
        return;
      }

      console.error('Error searching cards and devices:', error);
      setSearchResults([]);
      setShowSuggestions(false);
    }
  };

  // Get card/device details
  const getCardDetails = async (uid) => {
    try {
      // Smart device type detection based on UID format
      // Mifare cards typically have longer UIDs (24+ hex characters)
      // OBU devices typically have shorter numeric IDs or specific formats
      const isMifareFormat = uid.length >= 16 && /^[0-9A-Fa-f]+$/.test(uid);

      if (isMifareFormat) {
        // Try Mifare first for Mifare-like UIDs
        try {
          const response = await API.get('/wallet/check-balance', {
            params: { type: 'card_uid', value: uid },
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          });

          if (response.data) {
            return {
              ...response.data,
              uid: response.data.card_uid || uid,
              type: 'mifare',
              card_type: response.data.card_type || 'Mifare'
            };
          }
        } catch (err) {
          // If 404, try as OBU device
          if (err.response && err.response.status !== 404) {
            throw err;
          }
        }

        // Fallback: try as OBU Device
        const response = await API.get('/wallet/check-balance', {
          params: { type: 'obu_device_id', value: uid },
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });

        if (response.data) {
          return {
            ...response.data,
            uid: response.data.device_id || response.data.obu_device_id || uid,
            type: 'obu',
            card_type: response.data.device_type || 'OBU'
          };
        }
      } else {
        // Try OBU first for non-Mifare-like UIDs
        try {
          const response = await API.get('/wallet/check-balance', {
            params: { type: 'obu_device_id', value: uid },
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          });

          if (response.data) {
            return {
              ...response.data,
              uid: response.data.device_id || response.data.obu_device_id || uid,
              type: 'obu',
              card_type: response.data.device_type || 'OBU'
            };
          }
        } catch (err) {
          // If 404, try as Mifare
          if (err.response && err.response.status !== 404) {
            throw err;
          }
        }

        // Fallback: try as Mifare Card
        const response = await API.get('/wallet/check-balance', {
          params: { type: 'card_uid', value: uid },
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });

        if (response.data) {
          return {
            ...response.data,
            uid: response.data.card_uid || uid,
            type: 'mifare',
            card_type: response.data.card_type || 'Mifare'
          };
        }
      }

      return null;
    } catch (error) {
      if (error.response && error.response.status === 404) {
        console.debug(`Card/device details not found for uid=${uid} (404).`);
        return null;
      }
      console.error('Error fetching card details:', error);
      return null;
    }
  };

  // Search vehicles by license plate
  const searchVehiclesByPlate = async (query) => {
    if (!query.trim()) {
      setPlateSearchResults([]);
      setShowPlateSuggestions(false);
      return;
    }

    try {
      const response = await API.get('/vehicles/search-by-plate', {
        params: { query },
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      // Debug: Log received vehicle data
      console.log('🔍 Frontend received vehicles:', response.data);
      if (response.data && response.data.length > 0) {
        console.log('🚗 First vehicle details:', {
          license_plate: response.data[0].license_plate,
          province: response.data[0].owner_province,
          model: response.data[0].vehicle_model,
          color: response.data[0].vehicle_color
        });
      }

      setPlateSearchResults(response.data || []);
      setShowPlateSuggestions(true);
    } catch (error) {
      console.error('Error searching vehicles by license plate:', error);
      setPlateSearchResults([]);
      setShowPlateSuggestions(false);
    }
  };

  // Handle plate search input change
  const handlePlateSearchChange = (e) => {
    const query = e.target.value;
    setPlateSearchQuery(query);

    // Clear selected vehicle if search is cleared
    if (!query.trim()) {
      setSelectedVehicle(null);
      setSelectedCard(null);
      setFormData(prev => ({ ...prev, cardUid: '', cardType: '' }));
    }

    // Debounce search
    clearTimeout(window.plateSearchTimeout);
    window.plateSearchTimeout = setTimeout(() => {
      searchVehiclesByPlate(query);
    }, 300);
  };

  // Handle vehicle selection from plate search
  const handleVehicleSelect = async (vehicle) => {
    setSelectedVehicle(vehicle);
    setPlateSearchQuery(vehicle.license_plate);
    setShowPlateSuggestions(false);

    const enrichDevice = async (dev) => {
      const raw = (typeof dev === 'string') ? { uid: dev, type: 'mifare' } : dev;
      try {
        const details = await getCardDetails(raw.uid);
        if (details) return { ...raw, ...details, uid: details.uid || raw.uid };
      } catch (e) { }
      return raw;
    };

    if (vehicle.devices && vehicle.devices.length === 1) {
      const full = await enrichDevice(vehicle.devices[0]);
      setSelectedCard(full);
      const isFirst = checkIsFirstTopup(full);
      setIsFirstTopup(isFirst);
      setFormData(prev => ({ ...prev, cardUid: full.uid, cardType: full.type || 'mifare', description: `ຄ່າບົລິການຄ່າລົດຜ່ານຂົວທາງ-Topup ${vehicle.license_plate}` }));
      if (isFirst) applyFirstTopupAmount(full.type || 'mifare');
    } else if (vehicle.devices && vehicle.devices.length > 1) {
      const enriched = await Promise.all(vehicle.devices.map(enrichDevice));
      setSelectedVehicle({ ...vehicle, devices: enriched });
      setSelectedCard(null);
      setFormData(prev => ({ ...prev, cardUid: '', cardType: '', description: `ຄ່າບົລິການຄ່າລົດຜ່ານຂົວທາງ-Topup ${vehicle.license_plate}` }));
    } else {
      const deviceId = vehicle.device_id || vehicle.obu_sbu_id || vehicle.obu_id || vehicle.deviceUid || vehicle.mifare_card_uid;
      if (deviceId) {
        const inferredType = vehicle.mifare_card_uid ? 'mifare' : (vehicle.obu_sbu_id ? 'obu' : 'mifare');
        const full = await enrichDevice({ uid: deviceId, type: inferredType });
        setSelectedCard(full);
        const isFirst = checkIsFirstTopup(full);
        setIsFirstTopup(isFirst);
        setFormData(prev => ({ ...prev, cardUid: full.uid, cardType: full.type, description: `ຄ່າບົລິການຄ່າລົດຜ່ານຂົວທາງ-Topup ${vehicle.license_plate}` }));
        if (isFirst) applyFirstTopupAmount(full.type || 'mifare');
      } else {
        setSelectedCard(null);
        setFormData(prev => ({ ...prev, cardUid: '', cardType: '' }));
      }
    }
  };
  // Handle device selection from vehicle's devices
  const handleDeviceSelectFromVehicle = async (device) => {
    setSelectedCard(device);
    const isFirst = checkIsFirstTopup(device);
    setIsFirstTopup(isFirst);
    setFormData(prev => ({
      ...prev, cardUid: device.uid, cardType: device.type,
      description: selectedVehicle ? `ຄ່າບົລິການຄ່າລົດຜ່ານຂົວທາງ-Topup ${selectedVehicle.license_plate}` : prev.description
    }));
    if (isFirst) applyFirstTopupAmount(device.type);
  };
  // Handle search input change
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    // Clear selected card if search is cleared
    if (!query.trim()) {
      setSelectedCard(null);
      setFormData(prev => ({ ...prev, cardUid: '', cardType: '' }));
    }

    // Debounce search
    clearTimeout(window.searchTimeout);
    window.searchTimeout = setTimeout(() => {
      searchCardsAndDevices(query);
    }, 300);
  };

  // Handle card selection from suggestions
  const handleCardSelect = async (card) => {
    setSelectedCard(card);
    setSearchQuery(card.uid);

    // Check if this is a first topup (balance = 0)
    const isFirst = checkIsFirstTopup(card);
    setIsFirstTopup(isFirst);

    setFormData(prev => ({
      ...prev,
      cardUid: card.uid,
      cardType: card.type,
      description: `ຄ່າບໍລິການຄ່າລົດຜ່ານຂົວທາງ  ${card.uid}` // Auto-fill description
    }));

    // If first topup, auto-apply the first topup amount
    if (isFirst) {
      applyFirstTopupAmount(card.type);
    }

    setShowSuggestions(false);
  };

  // Handle manual search (when user types and presses Enter)
  const handleSearchSubmit = async () => {
    if (!searchQuery.trim()) return;

    const cardDetails = await getCardDetails(searchQuery.trim());
    if (cardDetails) {
      setSelectedCard(cardDetails);

      // Check if this is a first topup (balance = 0)
      const isFirst = checkIsFirstTopup(cardDetails);
      setIsFirstTopup(isFirst);

      setFormData(prev => ({
        ...prev,
        cardUid: cardDetails.uid,
        cardType: cardDetails.type,
        description: `ຄ່າບໍລິການຄ່າລົດຜ່ານຂົວທາງ  ${cardDetails.uid}` // Auto-fill description
      }));

      // If first topup, auto-apply the first topup amount
      if (isFirst) {
        applyFirstTopupAmount(cardDetails.type);
      }
    } else {
      toast.error(`❌ ${t('messages.card_not_found')}`);
      setSelectedCard(null);
      setIsFirstTopup(false);
      setFormData(prev => ({ ...prev, cardUid: '', cardType: '' }));
    }
    setShowSuggestions(false);
  };

  const handleAmountChange = (e) => {
    let raw = e.target.value.replace(/,/g, '').replace(/[^0-9]/g, '');
    if (!raw) raw = '';
    setFormData(prev => ({ ...prev, amount: raw }));
  };

  const formatAmountDisplay = (val) => {
    if (!val) return '';
    const num = parseInt(val.toString().replace(/,/g, ''), 10);
    if (isNaN(num)) return '';
    return num.toLocaleString('en-US');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    console.log('🔍 Validating form with formData:', formData);

    // Try to infer card UID and type from selectedCard or selectedVehicle if missing
    const effective = getEffectiveCard();
    const uid = effective.uid;
    const type = effective.type;

    console.log('💳 effective cardUid:', uid);
    console.log('🏷️ effective cardType:', type);

    if (!uid || !type) {
      console.error('❌ Validation failed: Missing cardUid or cardType');
      toast.error(`❌ ${t('topup.search_select_required')}`);
      return false;
    }
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      console.error('❌ Validation failed: Invalid amount');
      toast.error(`❌ ${t('messages.invalid_input')}`);
      return false;
    }
    const _minAmt = isFirstTopup ? (getFirstTopupPolicy(getEffectiveCard().type)?.totalAmount || 1000) : 1000;
    if (parseFloat(formData.amount) < _minAmt) {
      console.error('❌ Validation failed: Amount below minimum');
      if (isFirstTopup) {
        const _pol = getFirstTopupPolicy(getEffectiveCard().type);
        toast.error(`❌ First topup minimum: ${_minAmt.toLocaleString()} ₭ (Deposit ${_pol.deposit.toLocaleString()} + Balance ${_pol.balance.toLocaleString()})`);
      } else {
        toast.error(`❌ ${t('messages.min_amount')}`);
      }
      return false;
    }
    if (!formData.description.trim()) {
      console.error('❌ Validation failed: Missing description');
      toast.error(`❌ ${t('messages.required_field')}`);
      return false;
    }
    console.log('✅ Validation passed!');
    return true;
  };

  // Helper to determine the effective card UID/type to use (prefers explicit formData, then selectedCard, then vehicle-level fields)
  const getEffectiveCard = () => {
    // Prefer explicit selection from formData
    if (formData.cardUid && formData.cardType) {
      return { uid: formData.cardUid, type: formData.cardType };
    }

    // If a selectedCard object exists (from suggestions or device select), use it
    if (selectedCard && selectedCard.uid) {
      return { uid: selectedCard.uid, type: selectedCard.type || selectedCard.card_type || 'mifare' };
    }

    // If a vehicle is selected, attempt to derive device id from common fields
    if (selectedVehicle) {
      // If devices array exists and has exactly one entry, use it
      if (Array.isArray(selectedVehicle.devices) && selectedVehicle.devices.length === 1) {
        const dev = selectedVehicle.devices[0];
        const uid = typeof dev === 'string' ? dev : (dev.uid || dev.device_id || dev.id);
        // Use the type from the device object if available, otherwise infer from UID length
        const type = (dev && (dev.type || dev.card_type)) || 'mifare';
        if (uid) return { uid, type };
      }

      // Fallback to common vehicle-level fields
      const vid = selectedVehicle.device_id || selectedVehicle.mifare_card_uid || selectedVehicle.obu_sbu_id || selectedVehicle.obu_id || selectedVehicle.deviceUid || selectedVehicle.device;
      if (vid) {
        // Infer type based on which field was populated
        const inferredType = selectedVehicle.mifare_card_uid ? 'mifare' : (selectedVehicle.obu_sbu_id || selectedVehicle.obu_id ? 'obu' : 'mifare');
        return { uid: vid, type: inferredType };
      }
    }

    return { uid: null, type: null };
  };

  // Helper function to check if this is a first topup (balance = 0 AND status = 'assigned')
  const checkIsFirstTopup = (cardData) => {
    if (!cardData) return false;
    const balance = parseFloat(cardData.balance || 0);
    const status = (cardData.status || '').toLowerCase();
    // First topup requires: balance = 0 AND status = 'assigned'
    return (balance === 0 || balance <= 0) && status === 'assigned';
  };

  // Get first topup policy for card type
  const getFirstTopupPolicy = (cardType) => {
    if (cardType === 'mifare') return FIRST_TOPUP_POLICY.mifare;
    if (cardType === 'obu') return FIRST_TOPUP_POLICY.obu;
    return null;
  };

  // Handle first topup selection - auto-set amount based on policy
  const applyFirstTopupAmount = (cardType) => {
    const policy = getFirstTopupPolicy(cardType);
    if (policy) {
      setFormData(prev => ({
        ...prev,
        amount: policy.totalAmount.toString()
      }));
      toast.success(`🎫 First topup: Total ${policy.totalAmount.toLocaleString()} ₭ = Deposit ${policy.deposit.toLocaleString()} ₭ + Balance ${policy.balance.toLocaleString()} ₭`);
    }
  };

  const generateQR = async () => {
    if (!validateForm()) return;

    // Determine effective card values (in case they were inferred)
    const effective = getEffectiveCard();
    const cardUidToUse = effective.uid;
    const cardTypeToUse = effective.type;

    setLoading(true);
    try {
      // Call the integrated LDB API (running on port 5001)
      const qrApiUrl = `${process.env.REACT_APP_API_URL}/ldb/generate-qr`.replace('/api/api', '/api');

      console.log(`🔄 Generating ${formData.selectedBank} QR code...`);
      console.log(`📡 API URL: ${qrApiUrl}`);

      const response = await fetch(qrApiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          card_uid: cardUidToUse,
          amount: parseFloat(formData.amount),
          description: formData.description,
          mobile_num: '2097285066', // Default mobile number
          source: 'topup'
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        // New API returns qrCodeImage directly
        setQrImage(data.qrCodeImage || data.qrCodeString || data.qrCode);

        // Reference2 is always provided by new API
        const reference2 = data.reference2 || `TKB${Date.now()}`;

        setTransactionData({
          transactionId: data.transactionId,
          amount: data.amount,
          cardUid: data.card_uid || cardUidToUse,
          description: data.description || formData.description,
          bank: data.bank || formData.selectedBank,
          reference2: reference2
        });

        console.log('📊 Transaction Data:', {
          transactionId: data.transactionId,
          reference2: reference2,
          amount: data.amount,
          bank: data.bank
        });

        // Initialize payment status as pending after QR generation
        setPaymentStatus('pending');

        // Auto-scroll to QR code display
        setTimeout(() => {
          if (qrDisplayRef.current) {
            qrDisplayRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);

        // Start checking payment status periodically
        // Pass all necessary data to avoid state timing issues
        const txnData = {
          transactionId: data.transactionId,
          amount: data.amount,
          cardUid: data.card_uid || cardUidToUse,
          description: data.description || formData.description,
          bank: data.bank || formData.selectedBank,
          reference2: reference2
        };
        startPaymentStatusCheck(data.transactionId, txnData, formData.selectedBank);

        toast.success(`✅ ${translate('topup.qr_generated_success', { bank: formData.selectedBank })}`);
      } else {
        throw new Error(data.error || 'QR generation failed');
      }

    } catch (error) {
      console.error('QR generation error:', error);

      // Show specific error message
      if (error.message.includes('Failed to fetch') || error.message.includes('ECONNREFUSED')) {
        toast.error(`❌ ${translate('topup.qr_server_unavailable', { bank: formData.selectedBank })}`);
      } else {
        toast.error(`❌ ${translate('topup.qr_generate_failed', { bank: formData.selectedBank })}: ${error.message}`);
      }

      // Generate mock QR for demo purposes when real API fails
      const mockQRData = `data:image/svg+xml;base64,${btoa(`
        <svg width="200" height="200" xmlns="http://www.w3.org/2000/svg">
          <rect width="200" height="200" fill="white"/>
          <rect x="10" y="10" width="180" height="180" fill="white" stroke="black" stroke-width="2"/>
          <text x="100" y="30" text-anchor="middle" font-family="Arial" font-size="14" fill="red" font-weight="bold">DEMO MODE</text>
          <text x="100" y="60" text-anchor="middle" font-family="Arial" font-size="12" fill="black">${formData.selectedBank} Bank</text>
          <text x="100" y="80" text-anchor="middle" font-family="Arial" font-size="10" fill="black">Amount: ${parseFloat(formData.amount).toLocaleString()} LAK</text>
          <text x="100" y="100" text-anchor="middle" font-family="Arial" font-size="8" fill="black">Card: ${formData.cardUid}</text>
          <rect x="50" y="120" width="100" height="60" fill="black"/>
          <rect x="60" y="130" width="80" height="40" fill="white"/>
          <text x="100" y="155" text-anchor="middle" font-family="Arial" font-size="8" fill="black">QR CODE</text>
          <text x="100" y="190" text-anchor="middle" font-family="Arial" font-size="6" fill="red">Real API unavailable</text>
        </svg>
      `)}`;

      setQrImage(mockQRData);

      const demoTxnId = `DEMO_${formData.selectedBank}_${Date.now()}`;
      const demoRef2 = `TKB${Date.now()}`;

      setTransactionData({
        transactionId: demoTxnId,
        amount: parseFloat(formData.amount),
        cardUid: formData.cardUid,
        description: formData.description,
        bank: formData.selectedBank,
        reference2: demoRef2
      });

      console.log('🎭 Demo Transaction Data:', {
        transactionId: demoTxnId,
        reference2: demoRef2,
        amount: parseFloat(formData.amount),
        bank: formData.selectedBank
      });

      // Even in demo mode, show pending status
      setPaymentStatus('pending');

      // For demo mode, simulate status progression after delay
      setTimeout(() => {
        if (paymentStatus === 'pending') {
          setPaymentStatus('demo-ready');
        }
      }, 3000);

      toast.error(`⚠️ ${t('topup.demo_mode_warning')}`);
    } finally {
      setLoading(false);
    }
  };

  const processCashTopup = async () => {
    if (!validateForm()) return;

    setLoading(true);

    const effective = getEffectiveCard();
    const requestData = {
      uid: effective.uid,
      type: effective.type,
      amount: parseFloat(formData.amount),
      description: formData.description + ' (Cash)',
      method: 'Cash' // ✅ Changed to 'Cash' (capital C)
    };

    console.log('💰 [processCashTopup] Sending request:', requestData);
    console.log('📋 Full formData state:', formData);

    try {
      const response = await API.post('/admin/cards-devices/topup', requestData, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      console.log('✅ [processCashTopup] Response:', response.data);

      // Show success with transaction_id if available
      const txnId = response.data?.transaction_id;
      const newBalance = response.data?.new_balance;

      if (txnId) {
        toast.success(`✅ ${t('topup.cash_topup_success')} - Transaction ID: ${txnId}`);
        console.log('✅ Cash topup transaction ID:', txnId);

        // Open PDF receipt with authentication
        try {
          const pdfResponse = await API.get(
            `/admin/topup-receipt-pdf/${txnId}`,
            {
              headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
              responseType: 'blob'
            }
          );

          // Create blob URL and open in new tab
          const blob = new Blob([pdfResponse.data], { type: 'application/pdf' });
          const blobUrl = window.URL.createObjectURL(blob);
          window.open(blobUrl, '_blank');

          // Clean up blob URL after a delay
          setTimeout(() => window.URL.revokeObjectURL(blobUrl), 10000);
        } catch (pdfError) {
          console.error('📤 PDF generation error:', pdfError);
          // Don't fail the whole topup if PDF fails
        }
      } else {
        toast.success(`✅ ${t('topup.cash_topup_success')}`);
      }

      // Refresh selected card data to show updated balance
      if (selectedCard) {
        try {
          const updatedCardData = await getCardDetails(formData.cardUid);
          if (updatedCardData) {
            setSelectedCard(updatedCardData);
          }
        } catch (error) {
          console.log('Could not refresh card data:', error);
        }
      }

      // Reset form and clear all search fields
      setFormData({
        cardUid: '',
        cardType: '',
        amount: '',
        description: '',
        paymentMethod: 'cash',
        selectedBank: 'LDB'
      });
      setSelectedCard(null);
      setSearchQuery('');

      // Clear plate search fields
      setPlateSearchQuery('');
      setSelectedVehicle(null);
      setPlateSearchResults([]);
      setShowPlateSuggestions(false);

      console.log('✅ Form reset - all fields cleared');
    } catch (error) {
      console.error('❌ [processCashTopup] Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText,
        fullError: error
      });

      const errMsg = error.response?.data?.error || error.response?.data?.message || 'ບໍ່ສາມາດເພີ່ມເງິນໃນບັດໄດ້';
      toast.error(`❌ ${errMsg}`);

      if (error.response?.data?.sqlMessage) {
        console.error('📣 SQL Error:', error.response.data.sqlMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const confirmQRPayment = async () => {
    if (!transactionData) return;

    setLoading(true);
    try {
      console.log('🔄 Starting payment confirmation process...', {
        transactionId: transactionData.transactionId,
        cardUid: transactionData.cardUid,
        amount: transactionData.amount,
        bank: transactionData.bank,
        currentPaymentStatus: paymentStatus
      });

      // Step 1: Check payment status via LDB inquiry API (real-time check)
      console.log('🔍 [Confirm] Step 1: Checking payment status via LDB inquiry API...');
      const reqTxnId = transactionData.transactionId;
      const reference2 = transactionData.reference2 || `TKB${transactionData.transactionId.substring(0, 8)}`;

      let isPaid = false;
      let inquiryStatus = 'unknown';

      if (transactionData.bank === 'LDB') {
        // Query LDB API to get real-time payment status
        try {
          const inquiryResult = await queryTransaction(reqTxnId, reference2, 'LAO_QR');
          console.log('📊 [Confirm] LDB Inquiry result:', inquiryResult);

          if (inquiryResult.success) {
            isPaid = inquiryResult.isPaid;
            inquiryStatus = inquiryResult.status || 'unknown';
            console.log(`💰 [Confirm] Payment Status: ${inquiryStatus} (isPaid: ${isPaid})`);
          } else {
            console.error('❌ [Confirm] LDB inquiry failed:', inquiryResult.error);
            toast.error(`❌ ${t('messages.inquiry_failed')}: ${inquiryResult.error}`);
            setLoading(false);
            return;
          }
        } catch (inquiryError) {
          console.error('❌ [Confirm] LDB inquiry error:', inquiryError);
          toast.error(`❌ ${t('messages.inquiry_failed')}: ${inquiryError.message}`);
          setLoading(false);
          return;
        }
      } else {
        // For non-LDB banks, trust the current paymentStatus
        isPaid = paymentStatus === 'paid';
        inquiryStatus = paymentStatus;
      }

      // Step 2: Check if payment is confirmed
      if (!isPaid) {
        console.log(`⏳ [Confirm] Payment not yet confirmed. Status: ${inquiryStatus}`);

        // Show appropriate message based on status
        let statusMessage = '';
        if (inquiryStatus === 'PNDG' || inquiryStatus === 'pending' || inquiryStatus === 'PENDING') {
          statusMessage = 'Payment is pending - please complete payment in your banking app first';
        } else if (inquiryStatus === 'PROCESSING') {
          statusMessage = 'Payment is being processed - please wait a moment';
        } else if (inquiryStatus === 'unknown' || inquiryStatus === 'UNKNOWN') {
          statusMessage = 'Payment status unknown - please check your payment and try again';
        } else {
          statusMessage = `Payment not confirmed yet - Status: ${inquiryStatus}`;
        }

        // Use custom toast instead of toast.warning (which doesn't exist)
        toast(`⚠️ ${statusMessage}`, {
          icon: '⚠️',
          style: {
            background: '#FEF3C7',
            color: '#92400E',
          },
        });

        setPaymentStatus('pending');
        setLoading(false);
        return;
      }

      console.log('✅ [Confirm] Payment confirmed! Proceeding with topup...');
      setPaymentStatus('paid');

      // Step 3: Confirm the payment and topup card via main backend API
      console.log('💳 [Confirm] Step 2: Calling backend to topup card...');
      try {
        const confirmResponse = await API.post('/admin/cards-devices/topup', {
          uid: transactionData.cardUid,
          type: formData.cardType,
          amount: parseFloat(transactionData.amount),
          description: `${transactionData.description} (${transactionData.bank} QR Payment - ${transactionData.transactionId})`,
          method: 'LaoQr' // ✅ Changed to 'LaoQr' (capital L and Q)
        }, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });

        console.log('✅ [Confirm] Backend response:', confirmResponse.data);

        // Log transaction_id from backend if available
        if (confirmResponse.data?.transaction_id) {
          console.log('🆔 [Confirm] Backend Transaction ID:', confirmResponse.data.transaction_id);
        }

        // Check if already processed
        if (confirmResponse.data.message && confirmResponse.data.message.includes('already')) {
          console.log('🔁 [Confirm] Transaction already processed, showing success without double topup');
          setPaymentStatus('completed');
          toast.success(`✅ ${t('messages.payment_already_processed')}`);

          // Refresh card balance to show updated amount
          try {
            const updatedCardData = await getCardDetails(transactionData.cardUid);
            if (updatedCardData) {
              setSelectedCard(updatedCardData);
              console.log('✅ [Confirm] Card balance after confirmation:', updatedCardData.balance);
            }
          } catch (refreshError) {
            console.warn('⚠️ [Confirm] Could not refresh card balance:', refreshError.message);
          }

          // Clear QR code and reset form after successful confirmation
          console.log('🧹 [Confirm] Clearing QR code and resetting form...');
          setTimeout(() => {
            resetForm();
          }, 2000);

          setLoading(false);
          setShowConfirmModal(false);
          return;
        }

        // Topup successful
        console.log('✅ [Confirm] Topup processed successfully!');
        setPaymentStatus('completed');
        toast.success(`✅ ${t('messages.payment_confirmed_and_topped_up')}`);

        // Stop status check polling
        stopPaymentStatusCheck();

        // Refresh card balance to show updated amount
        try {
          const updatedCardData = await getCardDetails(transactionData.cardUid);
          if (updatedCardData) {
            setSelectedCard(updatedCardData);
            console.log('✅ [Confirm] New card balance after topup:', updatedCardData.balance);
          }
        } catch (refreshError) {
          console.warn('⚠️ [Confirm] Could not refresh card balance:', refreshError.message);
        }

        // Clear QR code and reset form after successful confirmation
        console.log('🧹 [Confirm] Clearing QR code and resetting form...');
        setTimeout(() => {
          resetForm();
        }, 2000);

        setLoading(false);
        setShowConfirmModal(false);
        return;

      } catch (qrApiError) {
        console.error('❌ [Confirm] Backend API error:', qrApiError);

        // Show specific error message
        if (qrApiError.message.includes('Failed to fetch') || qrApiError.message.includes('NetworkError')) {
          toast.error(`❌ ${t('messages.confirmation_failed')}: Backend server unavailable`);
        } else {
          toast.error(`❌ ${t('messages.confirmation_failed')}: ${qrApiError.message}`);
        }

        setLoading(false);
        setShowConfirmModal(false);
        return;
      }

    } catch (error) {
      // Catch any unexpected errors not handled by inner try-catch
      console.error('❌ [Confirm] Unexpected error in payment confirmation:', error);
      toast.error(`❌ ${t('error_messages.unexpected_error')}: ${error.message}`);
      setLoading(false);
      setShowConfirmModal(false);
    }
  };

  // Function to start periodic payment status checking using LDB Query Transaction API
  const startPaymentStatusCheck = (transactionId, txnData, selectedBank) => {
    // Clear any existing interval
    if (statusCheckInterval) {
      clearInterval(statusCheckInterval);
    }

    console.log('🔄 Starting automatic payment status polling with LDB API...');
    console.log('Transaction ID:', transactionId);
    console.log('Bank (from params):', selectedBank);
    console.log('Bank (from txnData):', txnData?.bank);
    console.log('Transaction Data:', txnData);

    // Only use LDB inquiry API for LDB bank - check parameter first
    const useLdbApi = (txnData?.bank === 'LDB') || (selectedBank === 'LDB') || (transactionData?.bank === 'LDB') || (formData.selectedBank === 'LDB');

    console.log('✅ useLdbApi:', useLdbApi);

    // Check status every 5 seconds for LDB API (less frequent to avoid rate limits)
    const interval = setInterval(async () => {
      try {
        if (useLdbApi) {
          // Use real LDB queryTransaction API
          console.log('🔍 Auto-checking payment status via LDB API...');

          // Use passed txnData or fall back to state (for when called from other places)
          const currentTxnData = txnData || transactionData;
          if (!currentTxnData) {
            console.warn('⚠️ No transaction data available for polling');
            return;
          }

          const reqTxnId = currentTxnData.transactionId;
          const reference2 = currentTxnData.reference2 || `TKB${transactionId.substring(0, 8)}`;

          console.log('📋 [Auto-Poll] Query Parameters:');
          console.log('   - Transaction ID (reqTxnId):', reqTxnId);
          console.log('   - Reference2:', reference2);
          console.log('   - QR Type: LAO_QR');
          console.log('   - Full URL will be: ' + (process.env.REACT_APP_API_URL) + '/ldb/query-transaction/' + reqTxnId + '?reference2=' + reference2);

          // Call LDB inquiry API via backend proxy
          const inquiryResult = await queryTransaction(reqTxnId, reference2, 'LAO_QR');

          console.log('🔍 [Auto-Poll] Inquiry result:', inquiryResult);

          if (inquiryResult.success) {
            // Backend already determined isPaid status
            const isPaid = inquiryResult.isPaid;
            const status = inquiryResult.status;

            console.log(`📊 [Auto-Poll] Payment Status: ${status} (isPaid: ${isPaid})`);

            if (isPaid) {
              console.log('🎉 Payment PAID detected via LDB API! Auto-confirming and clearing form...');
              setPaymentStatus('paid');

              // Stop checking immediately
              clearInterval(interval);
              setStatusCheckInterval(null);

              // Show success message
              toast.success(`✅ ${t('messages.payment_detected')} - Auto-confirming payment...`);

              // Auto-confirm payment - topup card balance via main backend API
              try {
                const currentTxnData = txnData || transactionData;
                const confirmResponse = await API.post('/admin/cards-devices/topup', {
                  uid: currentTxnData.cardUid,
                  type: formData.cardType,
                  amount: parseFloat(currentTxnData.amount),
                  description: `${currentTxnData.description} (LDB QR Payment - ${currentTxnData.transactionId})`,
                  method: 'LaoQr' // ✅ Changed to 'LaoQr'
                }, {
                  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                });

                console.log('✅ [Auto-Confirm] Payment confirmed and card topped up');

                // Log transaction_id from backend if available
                if (confirmResponse.data?.transaction_id) {
                  console.log('🆔 [Auto-Confirm] Backend Transaction ID:', confirmResponse.data.transaction_id);
                }

                // Refresh card balance
                let updatedCardData = null;
                if (formData.cardUid) {
                  try {
                    updatedCardData = await getCardDetails(formData.cardUid);
                    if (updatedCardData) {
                      setSelectedCard(updatedCardData);
                      console.log('✅ Card balance after topup:', updatedCardData.balance);
                    }
                  } catch (refreshError) {
                    console.warn('⚠️ Could not refresh card balance:', refreshError.message);
                  }
                }

                // Generate receipt for QR payment
                const qrReceipt = {
                  transactionId: currentTxnData.transactionId,
                  reference2: currentTxnData.reference2,
                  timestamp: new Date(),
                  cardUid: currentTxnData.cardUid,
                  cardType: formData.cardType,
                  amount: parseFloat(currentTxnData.amount),
                  description: currentTxnData.description,
                  paymentMethod: 'QR',
                  bank: currentTxnData.bank || 'LDB',
                  previousBalance: updatedCardData?.balance ? (updatedCardData.balance - parseFloat(currentTxnData.amount)) : 0,
                  newBalance: updatedCardData?.balance || 0,
                  licensePlate: selectedVehicle?.license_plate || plateSearchQuery || null,
                  operatorName: JSON.parse(localStorage.getItem('user'))?.name || 'Admin'
                };

                // Show success message
                toast.success(`✅ Payment confirmed! Card topped up successfully.`, {
                  duration: 2000
                });

                // Show receipt after short delay
                setTimeout(() => {
                  setReceiptData(qrReceipt);
                  setShowReceipt(true);

                  // Auto-close receipt after 5 seconds
                  setTimeout(() => {
                    setShowReceipt(false);
                    setReceiptData(null);
                  }, 5000);
                }, 1000);

              } catch (confirmError) {
                console.error('❌ [Auto-Confirm] Error:', confirmError);
                console.log('📝 [Auto-Confirm] Payment detected but topup might have already been processed');

                // Try to refresh card balance anyway
                let updatedCardData = null;
                if (formData.cardUid) {
                  try {
                    updatedCardData = await getCardDetails(formData.cardUid);
                    if (updatedCardData) {
                      setSelectedCard(updatedCardData);
                      console.log('✅ Card balance refreshed:', updatedCardData.balance);
                    }
                  } catch (refreshError) {
                    console.warn('⚠️ Could not refresh card balance:', refreshError.message);
                  }
                }

                // Generate receipt for auto-confirmed payment
                const autoReceipt = {
                  transactionId: currentTxnData.transactionId,
                  reference2: currentTxnData.reference2,
                  timestamp: new Date(),
                  cardUid: currentTxnData.cardUid,
                  cardType: formData.cardType,
                  amount: parseFloat(currentTxnData.amount),
                  description: currentTxnData.description,
                  paymentMethod: 'QR',
                  bank: currentTxnData.bank || 'LDB',
                  previousBalance: updatedCardData?.balance ? (updatedCardData.balance - parseFloat(currentTxnData.amount)) : 0,
                  newBalance: updatedCardData?.balance || 0,
                  licensePlate: selectedVehicle?.license_plate || plateSearchQuery || null,
                  operatorName: JSON.parse(localStorage.getItem('user'))?.name || 'Admin'
                };

                toast.success(`✅ Payment detected! Card balance updated.`, {
                  duration: 2000
                });

                // Show receipt after short delay
                setTimeout(() => {
                  setReceiptData(autoReceipt);
                  setShowReceipt(true);

                  // Auto-close receipt after 5 seconds
                  setTimeout(() => {
                    setShowReceipt(false);
                    setReceiptData(null);
                  }, 5000);
                }, 1000);
              }

              // Always clear form after payment is detected, regardless of topup result
              setTimeout(() => {
                console.log('🧹 [Auto-Confirm] Clearing QR and resetting form...');
                resetForm();
              }, 2000);

            } else if (status.toLowerCase() === 'processing' || status.toLowerCase() === 'pending') {
              console.log('⏳ Payment is still pending/processing...');
              setPaymentStatus('pending');
            } else if (status.toLowerCase() === 'failed' || status.toLowerCase() === 'error') {
              console.log('❌ Payment failed.');
              setPaymentStatus('failed');
              clearInterval(interval);
              setStatusCheckInterval(null);
              toast.error(`❌ ${t('messages.payment_failed')}`);
            }
          } else {
            console.warn('⚠️ LDB inquiry failed:', inquiryResult.error);
            // Continue polling even if inquiry fails temporarily
          }
        } else {
          // This should not happen as only LDB is supported
          console.warn('⚠️ Non-LDB bank detected - this should not happen');

          const response = await fetch(`${process.env.REACT_APP_API_URL}/topup/check-status/${transactionId}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json'
            }
          });

          if (response.ok) {
            const statusData = await response.json();
            console.log('📊 Local API status:', statusData);

            if (statusData.webhook_status === 'paid' || statusData.status === 'paid') {
              console.log('🎉 Payment detected as paid via local API! Auto-clearing form...');
              setPaymentStatus('paid');

              // Stop checking
              clearInterval(interval);
              setStatusCheckInterval(null);

              // Refresh card data
              if (formData.cardUid) {
                try {
                  const updatedCardData = await getCardDetails(formData.cardUid);
                  if (updatedCardData) {
                    setSelectedCard(updatedCardData);
                    console.log('✅ Card balance after topup:', updatedCardData.balance);
                  }
                } catch (refreshError) {
                  console.warn('Could not refresh card balance:', refreshError.message);
                }
              }

              // Show success and clear form
              toast.success(`✅ Payment confirmed! Card topped up successfully. Clearing form...`, {
                duration: 3000
              });

              // Clear QR and reset form after 2 seconds
              setTimeout(() => {
                console.log('🧹 [Auto-Confirm] Clearing QR and resetting form...');
                resetForm();
              }, 2000);
            } else if (statusData.status === 'processing') {
              setPaymentStatus('processing');
            } else if (statusData.status === 'failed') {
              setPaymentStatus('failed');
              clearInterval(interval);
              setStatusCheckInterval(null);
              toast.error(`❌ ${t('messages.payment_failed')}`);
            }
          }
        }
      } catch (error) {
        console.warn('⚠️ Could not check payment status:', error.message);
        // Continue polling even if there's an error
      }
    }, useLdbApi ? 5000 : 3000); // 5 seconds for LDB API, 3 seconds for local API

    setStatusCheckInterval(interval);

    // Stop checking after 10 minutes to avoid infinite polling
    setTimeout(() => {
      clearInterval(interval);
      setStatusCheckInterval(null);
      console.log('⏱️ Automatic polling stopped after 10 minutes timeout');
    }, 600000); // 10 minutes
  };

  // Function to stop payment status checking
  const stopPaymentStatusCheck = () => {
    if (statusCheckInterval) {
      clearInterval(statusCheckInterval);
      setStatusCheckInterval(null);
    }
  };

  // Helper function to reset form after successful payment
  const resetForm = () => {
    console.log('🧹 Resetting form after successful payment');
    setFormData({
      cardUid: '',
      cardType: '',
      amount: '',
      description: '',
      paymentMethod: 'cash',
      selectedBank: 'LDB'
    });
    setSelectedCard(null);
    setSearchQuery('');
    setIsFirstTopup(false);

    // Clear plate search fields
    setPlateSearchQuery('');
    setSelectedVehicle(null);
    setPlateSearchResults([]);
    setShowPlateSuggestions(false);

    setQrImage(null);
    setTransactionData(null);
    setPaymentStatus(null);
    stopPaymentStatusCheck();
    setShowConfirmModal(false);
  };

  // Check payment status using LDB Inquiry API
  const checkPaymentStatus = async () => {
    if (!transactionData || !transactionData.transactionId) {
      toast.error('❌ No transaction data available');
      return;
    }

    console.log('🔍 Checking payment status via LDB Inquiry API...');
    setCheckingPayment(true);

    try {
      // Extract reqTxnId and reference2 from transactionData
      // Assuming transactionId contains the x-client-transaction-id
      const reqTxnId = transactionData.transactionId;
      const reference2 = transactionData.reference2 || `TKB${transactionData.transactionId.substring(0, 8)}`;

      console.log('Query Parameters:', { reqTxnId, reference2 });

      // Call LDB inquiry API via backend proxy
      const inquiryResult = await queryTransaction(reqTxnId, reference2, 'LAO_QR');

      console.log('📊 [Manual Check] Inquiry result:', inquiryResult);

      if (inquiryResult.success) {
        // Backend already determined isPaid status
        const isPaid = inquiryResult.isPaid;
        const status = inquiryResult.status;

        console.log(`💰 [Manual Check] Payment Status: ${status} (isPaid: ${isPaid})`);

        if (isPaid) {
          setPaymentStatus('paid');
          toast.success(`✅ ${t('messages.payment_confirmed')}: ${status}`);

          // Refresh card balance
          if (selectedCard && formData.cardUid) {
            try {
              const updatedCardData = await getCardDetails(formData.cardUid);
              if (updatedCardData) {
                setSelectedCard(updatedCardData);
                console.log('✅ Card balance refreshed:', updatedCardData.balance);
              }
            } catch (error) {
              console.warn('Could not refresh card balance:', error);
            }
          }
        } else {
          setPaymentStatus('pending');

          // Show appropriate message based on status
          let statusMessage = '';
          if (status === 'PNDG' || status === 'PENDING') {
            statusMessage = 'Payment is pending - waiting for payment';
          } else if (status === 'PROCESSING') {
            statusMessage = 'Payment is being processed';
          } else if (status === 'UNKNOWN') {
            statusMessage = 'Payment status unknown - please try again';
          } else {
            statusMessage = `Payment status: ${status}`;
          }

          toast(`⏳ ${statusMessage}`, {
            icon: '⏳',
            style: {
              background: '#FEF3C7',
              color: '#92400E',
            },
          });
        }
      } else {
        toast.error(`❌ ${t('messages.inquiry_failed')}: ${inquiryResult.error}`);
      }
    } catch (error) {
      console.error('❌ Error checking payment status:', error);
      toast.error(`❌ ${t('error_messages.unexpected_error')}: ${error.message}`);
    } finally {
      setCheckingPayment(false);
    }
  };

  // Print the QR code (opens a print window formatted for thermal printers)
  const printQRCode = () => {
    if (!qrImage || !transactionData) {
      toast.error('❌ No QR available to print');
      return;
    }

    // Build receipt data similar to auto-confirm path so ThermalReceipt can render it
    const prevBal = selectedCard?.balance ?? 0;
    const amount = parseFloat(transactionData.amount || 0);
    const newBal = prevBal + amount;

    // set paper size to 50mm for printing this receipt
    setPrintWidth('50mm');

    const qrReceipt = {
      transactionId: transactionData.transactionId,
      reference2: transactionData.reference2,
      timestamp: new Date(),
      cardUid: transactionData.cardUid,
      cardType: formData.cardType,
      amount: amount,
      description: transactionData.description,
      paymentMethod: 'QR',
      bank: transactionData.bank || 'LDB',
      previousBalance: prevBal,
      newBalance: newBal,
      licensePlate: selectedVehicle?.license_plate || plateSearchQuery || null,
      operatorName: JSON.parse(localStorage.getItem('user'))?.name || 'Admin',
      qrImage: qrImage,
      autoPrint: false,
      paperWidth: '50mm'
    };

    setReceiptData(qrReceipt);
    setShowReceipt(true);
  };


  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">
          💸 {t('topup.title')}
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          {t('topup.subtitle')}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Topup Form */}
        <div className="space-y-4">
          {/* Search Mode Toggle */}
          <div className="bg-gray-100 dark:bg-gray-700 p-3 rounded-lg">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              🔍 Search Method
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => {
                  setSearchMode('plate');
                  setSearchQuery('');
                  setSelectedCard(null);
                  setSearchResults([]);
                  setShowSuggestions(false);
                }}
                className={`py-2 px-4 rounded-lg font-medium transition-colors ${searchMode === 'plate'
                  ? 'bg-blue-500 text-white'
                  : 'bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-500'
                  }`}
              >
                🚗 By License Plate
              </button>
              <button
                onClick={() => {
                  setSearchMode('device');
                  setPlateSearchQuery('');
                  setSelectedVehicle(null);
                  setPlateSearchResults([]);
                  setShowPlateSuggestions(false);
                }}
                className={`py-2 px-4 rounded-lg font-medium transition-colors ${searchMode === 'device'
                  ? 'bg-blue-500 text-white'
                  : 'bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-500'
                  }`}
              >
                💳 By Card/Device
              </button>
            </div>
          </div>

          {/* License Plate Search */}
          {searchMode === 'plate' && (
            <div className="relative" ref={plateSearchRef}>
              <label className="block text-xl font-bold text-blue-600 dark:text-blue-400 mb-2">
                🚗 Search by License Plate
              </label>
              <input
                type="text"
                value={plateSearchQuery}
                onChange={handlePlateSearchChange}
                placeholder="Enter license plate (e.g., ມມ2211)"
                className="w-full p-5 text-2xl font-bold border-4 border-blue-500 dark:border-blue-400 rounded-xl focus:ring-4 focus:ring-blue-300 dark:bg-gray-700 dark:text-white shadow-lg"
              />

              {/* Plate search suggestions dropdown - Enhanced with Province, Model, Color */}
              {showPlateSuggestions && plateSearchResults.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-700 border-2 border-blue-400 dark:border-blue-500 rounded-lg shadow-2xl max-h-96 overflow-y-auto">
                  <div className="sticky top-0 bg-yellow-50 dark:bg-yellow-900/30 border-b-2 border-yellow-300 dark:border-yellow-700 p-2">
                    <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200 flex items-center gap-2">
                      ⚠️ Found {plateSearchResults.length} vehicle(s) with this plate. Please select the correct one:
                    </p>
                  </div>
                  {plateSearchResults && plateSearchResults.map((vehicle) => (
                    <div
                      key={vehicle.id}
                      className="p-4 hover:bg-blue-50 dark:hover:bg-blue-900/30 cursor-pointer border-b-2 border-gray-200 dark:border-gray-600 last:border-b-0 transition-colors"
                      onClick={() => handleVehicleSelect(vehicle)}
                    >
                      <div className="space-y-2">
                        {/* Header: License Plate and Vehicle Type */}
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xl font-bold text-blue-600 dark:text-blue-400">
                                🚗 {vehicle.license_plate}
                              </span>
                            </div>
                            <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              {vehicle.vehicle_type}
                            </div>
                            {/* Province and Brand on same line - Prominent display */}
                            <div className="flex items-center gap-3 flex-wrap">
                              {vehicle.owner_province && (
                                <span className="text-base font-bold text-purple-600 dark:text-purple-400">
                                  📍 ແຂວງ: {vehicle.owner_province}
                                </span>
                              )}
                              {vehicle.vehicle_model && vehicle.vehicle_model !== 'N/A' && (
                                <span className="text-base font-bold text-orange-600 dark:text-orange-400">
                                  🚙 ສີ: {vehicle.vehicle_model}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Details Grid - Owner and Color only */}
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          {/* Owner */}
                          <div>
                            <span className="text-gray-500 dark:text-gray-400">👤 Owner:</span>
                            <span className="ml-1 font-medium text-gray-900 dark:text-white">{vehicle.owner_name}</span>
                          </div>

                          {/* Color */}
                          {vehicle.vehicle_color && vehicle.vehicle_color !== 'N/A' && (
                            <div>
                              <span className="text-gray-500 dark:text-gray-400">🎨 Color:</span>
                              <span className="ml-1 font-medium text-gray-900 dark:text-white">{vehicle.vehicle_color}</span>
                            </div>
                          )}
                        </div>

                        {/* Devices */}
                        <div className="pt-2 border-t border-gray-200 dark:border-gray-600">
                          <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Devices:</div>
                          <div className="flex gap-2 flex-wrap">
                            {vehicle.devices && Array.isArray(vehicle.devices) && vehicle.devices.map((device, idx) => (
                              <span
                                key={idx}
                                className={`text-xs px-3 py-1 rounded-full font-medium ${device.type === 'mifare'
                                  ? 'bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-200'
                                  : 'bg-purple-100 text-purple-800 dark:bg-purple-800 dark:text-purple-200'
                                  }`}
                              >
                                {device.type === 'mifare' ? '💳' : '📡'} {device.uid}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Device/Card Search */}
          {searchMode === 'device' && (
            <div className="relative" ref={searchRef}>
              <label className="block text-xl font-bold text-green-600 dark:text-green-400 mb-2">
                💳 Search MIFARE Card or OBU-SBU Device
              </label>
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                onKeyPress={(e) => e.key === 'Enter' && handleSearchSubmit()}
                placeholder={t('topup.search_placeholder')}
                className="w-full p-5 text-2xl font-bold border-4 border-green-500 dark:border-green-400 rounded-xl focus:ring-4 focus:ring-green-300 dark:bg-gray-700 dark:text-white shadow-lg"
              />

              {/* Search suggestions dropdown */}
              {showSuggestions && searchResults?.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {searchResults.map((item) => (
                    <div
                      key={`${item.type}-${item.uid}`}
                      className="p-3 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer border-b border-gray-200 dark:border-gray-600 last:border-b-0"
                      onClick={() => handleCardSelect(item)}
                    >
                      <div className="flex justify-between items-center">
                        <div>
                          <span className="font-medium text-gray-900 dark:text-white">{item.uid}</span>
                          <span className={`ml-2 px-2 py-1 text-xs rounded-full ${item.type === 'mifare'
                            ? 'bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-200'
                            : 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-200'
                            }`}>
                            {item.type === 'mifare' ? 'MIFARE' : 'OBU/SBU'}
                          </span>
                        </div>
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {t('vehicles.balance_remaining')}: {item.balance?.toLocaleString() || '0'} LAK
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Show selected vehicle and its devices - ONLY if multiple devices */}
          {selectedVehicle && searchMode === 'plate' && selectedVehicle.devices && selectedVehicle.devices.length > 1 && (
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900 dark:to-pink-900 p-4 rounded-lg border border-purple-200 dark:border-purple-700">
              <h3 className="font-medium text-purple-800 dark:text-purple-200 mb-3">
                🚗 Vehicle: {selectedVehicle.license_plate}
              </h3>
              <div className="text-sm text-purple-700 dark:text-purple-300 mb-3">
                <p>Type: {selectedVehicle.vehicle_type}</p>
                <p>Owner: {selectedVehicle.owner_name}</p>
                {selectedVehicle.owner_phone && <p>Phone: {selectedVehicle.owner_phone}</p>}
              </div>

              <div className="space-y-2">
                <p className="text-sm font-medium text-purple-800 dark:text-purple-200">
                  ⚠️ Multiple devices found - Select device to top-up:
                </p>
                {selectedVehicle.devices && Array.isArray(selectedVehicle.devices) && selectedVehicle.devices.map((device, idx) => (

                  <button
                    key={idx}
                    onClick={() => handleDeviceSelectFromVehicle(device)}
                    className={`w-full p-3 rounded-lg border-2 transition-all ${selectedCard?.uid === device.uid
                      ? device.type === 'mifare'
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900'
                        : 'border-green-500 bg-green-50 dark:bg-green-900'
                      : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                      }`}
                  >
                    <div className="flex justify-between items-center">
                      <div className="text-left">
                        <div className="font-medium text-gray-900 dark:text-white">
                          {device.type === 'mifare' ? '💳 MIFARE' : '📡 OBU/SBU'}: {device.uid}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          Type: {device.card_type} | Status: {device.status}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Balance
                        </div>
                        <div className="text-lg font-bold text-gray-900 dark:text-white">
                          {device.balance?.toLocaleString() || '0'} LAK
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {selectedCard && (
            <div className={`p-3 rounded-lg ${selectedCard.type === 'mifare'
              ? 'bg-blue-50 dark:bg-blue-900'
              : 'bg-green-50 dark:bg-green-900'
              }`}>
              <h3 className={`font-medium mb-1 ${selectedCard.type === 'mifare'
                ? 'text-blue-800 dark:text-blue-200'
                : 'text-green-800 dark:text-green-200'
                }`}>
                {selectedCard.type === 'mifare' ? `💳 ${t('topup.card_info')}` : `📡 ${t('topup.device_info')}`}
              </h3>
              <p className={`text-sm ${selectedCard.type === 'mifare'
                ? 'text-blue-700 dark:text-blue-300'
                : 'text-green-700 dark:text-green-300'
                }`}>
                UID/ID: {selectedCard.uid} | {t('vehicles.vehicle_type')}: {selectedCard.card_type} | {t('user.status') || 'Status'}: {selectedCard.status} |
                {t('vehicles.balance_remaining')}: {selectedCard.balance?.toLocaleString() || '0'} LAK
              </p>
            </div>
          )}

          {/* First Topup Policy Info */}
          {isFirstTopup && selectedCard && (
            <div className="bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-900/30 dark:to-yellow-900/30 p-4 rounded-lg border-2 border-amber-400 dark:border-amber-600">
              <h3 className="font-bold text-amber-800 dark:text-amber-200 flex items-center gap-2 mb-2">
                🎫 First Topup Policy - {selectedCard.type === 'mifare' ? 'Mifare Card' : 'OBU Device'}
              </h3>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-white dark:bg-gray-800 rounded-lg p-2">
                  <div className="text-xs text-gray-600 dark:text-gray-400">Total Payment</div>
                  <div className="text-lg font-bold text-amber-700 dark:text-amber-300">
                    {getFirstTopupPolicy(selectedCard.type)?.totalAmount.toLocaleString()} ₭
                  </div>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-lg p-2">
                  <div className="text-xs text-gray-600 dark:text-gray-400">Deposit</div>
                  <div className="text-lg font-bold text-red-600 dark:text-red-400">
                    {getFirstTopupPolicy(selectedCard.type)?.deposit.toLocaleString()} ₭
                  </div>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-lg p-2">
                  <div className="text-xs text-gray-600 dark:text-gray-400">Balance</div>
                  <div className="text-lg font-bold text-green-600 dark:text-green-400">
                    {getFirstTopupPolicy(selectedCard.type)?.balance.toLocaleString()} ₭
                  </div>
                </div>
              </div>
              <p className="text-xs text-amber-700 dark:text-amber-300 mt-2 text-center">
                ⚠️ Total = Deposit (kept) + Balance (added to card)
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('topup.amount')} {isFirstTopup && <span className="text-amber-600">(First Topup — min {getFirstTopupPolicy(selectedCard?.type)?.totalAmount.toLocaleString()} ₭)</span>}
            </label>
            <input
              type="text"
              inputMode="numeric"
              name="amount"
              value={formatAmountDisplay(formData.amount)}
              onChange={handleAmountChange}
              placeholder={t('topup.topup_amount_placeholder') || t('topup.amount')}
              className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white font-bold text-lg ${isFirstTopup
                ? 'border-amber-400 bg-amber-50 dark:bg-amber-900/30'
                : 'border-gray-300 dark:border-gray-600'
                }`}
            />
            <p className="text-xs text-gray-500 mt-1">
              {isFirstTopup
                ? `⚠️ Minimum: ${getFirstTopupPolicy(selectedCard?.type)?.totalAmount.toLocaleString()} ₭ (Deposit: ${getFirstTopupPolicy(selectedCard?.type)?.deposit.toLocaleString()} + Balance: ${getFirstTopupPolicy(selectedCard?.type)?.balance.toLocaleString()}). You may top up more.`
                : t('messages.min_amount')
              }
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('topup.description')}
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder={t('topup.description_placeholder') || t('topup.description')}
              rows={3}
              className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            />
          </div>

          {/* Direct Action Buttons for Payment */}
          <div className="flex gap-4 pt-4 mt-2 border-t border-gray-100 dark:border-gray-700">
            <button
              onClick={() => {
                setFormData(prev => ({ ...prev, paymentMethod: 'cash' }));
                setTimeout(() => processCashTopup(), 50);
              }}
              disabled={loading}
              className={`flex-1 py-4 px-4 rounded-xl font-bold transition-all duration-200 ${loading
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-green-500 hover:bg-green-600 shadow-sm'
                } text-white flex items-center justify-center gap-2`}
            >
              {loading ? `🔄 ${t('common.loading')}` : <><span className="text-xl">💵</span><span>{t('topup.cash_topup')}</span></>}
            </button>
            <button
              onClick={() => {
                setFormData(prev => ({ ...prev, paymentMethod: 'laoqr', selectedBank: 'LDB' }));
                setTimeout(() => generateQR(), 50);
              }}
              disabled={loading}
              className={`flex-1 py-4 px-4 rounded-xl font-bold transition-all duration-200 ${loading
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700 shadow-sm'
                } text-white flex items-center justify-center gap-2`}
            >
              {loading ? `🔄 ${t('topup.generating_qr')}` : <><span className="text-xl">📱</span><span>LDB QR Payment</span></>}
            </button>
          </div>

        </div>

        {/* QR Code Display */}
        <div className="space-y-4 max-h-[calc(100vh-120px)] overflow-y-auto">
          {qrImage && (
            <div ref={qrDisplayRef} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 text-center sticky top-0 z-10">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
                📱 {t('topup.qr_for_payment')}
              </h3>

              <div className="bg-white p-4 rounded-lg inline-block shadow-md">
                <img
                  src={qrImage}
                  alt="QR Code for Payment"
                  className="w-48 h-48 mx-auto"
                />
              </div>

              {transactionData && (
                <div className="mt-4 p-3 bg-white dark:bg-gray-600 rounded-lg">
                  {/* Compact Info - Line 1 */}
                  <div className="text-xs text-gray-700 dark:text-gray-300 mb-2 flex flex-wrap gap-x-3 gap-y-1">
                    <span><strong>🏦 Bank:</strong> {transactionData.bank}</span>
                    <span><strong>💵 Amount:</strong> {transactionData.amount.toLocaleString()} LAK</span>
                    <span><strong>💳 Card:</strong> {transactionData.cardUid}</span>
                  </div>

                  {/* Compact Info - Line 2 with Status */}
                  <div className="text-xs text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                    <span><strong>📝 Desc:</strong> {transactionData.description}</span>
                    <span className="ml-auto">
                      <strong>Status:</strong>{' '}
                      <span className={
                        paymentStatus === 'paid' ? 'text-green-600 dark:text-green-300 font-semibold'
                          : paymentStatus === 'pending' ? 'text-yellow-600 dark:text-yellow-300 animate-pulse'
                            : paymentStatus === 'processing' ? 'text-blue-600 dark:text-blue-300 animate-pulse'
                              : paymentStatus === 'demo-ready' ? 'text-purple-600 dark:text-purple-300'
                                : 'text-gray-600 dark:text-gray-300'
                      }>
                        {paymentStatus === 'paid' ? `✅ Paid`
                          : paymentStatus === 'pending' ? `🔍 Waiting`
                            : paymentStatus === 'processing' ? `⏳ Processing`
                              : paymentStatus === 'demo-ready' ? `🎭 Demo`
                                : '—'}
                      </span>
                    </span>
                  </div>

                  {/* Transaction ID & Reference2 - Single Line */}
                  <div className="flex items-center gap-2 mb-2 p-2 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900 dark:to-purple-900 rounded-lg border border-blue-200 dark:border-blue-700">
                    {/* Transaction ID */}
                    <div className="flex items-center gap-1 flex-1 min-w-0">
                      <span className="text-xs font-bold text-blue-700 dark:text-blue-300 whitespace-nowrap">🆔 ID:</span>
                      <code className="flex-1 px-2 py-1 bg-white dark:bg-gray-800 rounded text-xs font-mono truncate shadow-sm" title={transactionData.transactionId}>
                        {transactionData.transactionId}
                      </code>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(transactionData.transactionId);
                          toast.success('✅ ID Copied!');
                        }}
                        className="bg-blue-500 hover:bg-blue-600 text-white px-2 py-1 rounded text-xs transition-all hover:scale-105 shadow-sm"
                        title="Copy Transaction ID"
                      >
                        📋
                      </button>
                    </div>

                    {/* Reference2 */}
                    {transactionData.reference2 && (
                      <div className="flex items-center gap-1 border-l border-blue-300 dark:border-blue-600 pl-2">
                        <span className="text-xs font-bold text-purple-700 dark:text-purple-300 whitespace-nowrap">📝 Ref2:</span>
                        <code className="px-2 py-1 bg-white dark:bg-gray-800 rounded text-xs font-mono shadow-sm">{transactionData.reference2}</code>
                      </div>
                    )}
                  </div>

                  {/* Compact Payment Steps - 2 Lines */}
                  <div className="mt-2 p-2 bg-yellow-50 dark:bg-yellow-900 rounded text-xs">
                    <div className="text-yellow-800 dark:text-yellow-200 font-semibold mb-1">
                      🔄 Steps: 1️⃣ Scan QR → 2️⃣ Pay in app → 3️⃣ Click "Confirm Payment" below
                    </div>
                    <div className="text-yellow-700 dark:text-yellow-300 italic">
                      ℹ️ Use Transaction ID above to check payment status
                    </div>
                  </div>

                  <div className="mt-4 flex flex-col gap-2">
                    <div className="flex items-center gap-3 mb-2">
                      <label className="text-xs text-gray-600 dark:text-gray-300">Paper:</label>
                      <select
                        value={printWidth}
                        onChange={(e) => setPrintWidth(e.target.value)}
                        className="p-2 text-sm rounded border"
                      >
                        <option value="58mm">58mm</option>
                        <option value="72mm">72mm</option>
                        <option value="80mm">80mm</option>
                      </select>
                      <div className="text-xs text-gray-500">Choose thermal width for printing</div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={checkPaymentStatus}
                        disabled={checkingPayment || formData.selectedBank !== 'LDB'}
                        className={`flex-1 ${formData.selectedBank === 'LDB'
                          ? 'bg-blue-500 hover:bg-blue-600'
                          : 'bg-gray-400 cursor-not-allowed'
                          } text-white py-2 px-4 rounded-lg transition-colors duration-200 disabled:opacity-60`}
                        title={formData.selectedBank !== 'LDB' ? 'Only available for LDB bank' : 'Check payment status'}
                      >
                        {checkingPayment ? '🔄 Checking...' : '🔍 Check Payment'}
                      </button>
                      <button
                        onClick={() => setShowConfirmModal(true)}
                        className="flex-1 bg-green-500 hover:bg-green-600 text-white py-2 px-4 rounded-lg transition-colors duration-200"
                      >
                        ✅ {t('topup.confirm_payment')}
                      </button>
                    </div>
                    <button
                      onClick={printQRCode}
                      className="w-full bg-indigo-500 hover:bg-indigo-600 text-white py-2 px-4 rounded-lg transition-colors duration-200"
                    >
                      🖨️ Print QR
                    </button>

                    <button
                      onClick={() => {
                        setQrImage(null);
                        setTransactionData(null);
                        setPaymentStatus(null);
                        stopPaymentStatusCheck();
                      }}
                      className="w-full bg-gray-500 hover:bg-gray-600 text-white py-2 px-4 rounded-lg transition-colors duration-200"
                    >
                      ❌ {t('common.cancel')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {!qrImage && (
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-8 text-center">
              <div className="text-gray-400 mb-4">
                <svg className="w-16 h-16 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 4a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 13a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1H4a1 1 0 01-1-1v-3zM13 3a1 1 0 00-1 1v3a1 1 0 001 1h3a1 1 0 001-1V4a1 1 0 00-1-1h-3z" clipRule="evenodd" />
                </svg>
              </div>
              <p className="text-gray-500 dark:text-gray-400">
                {formData.paymentMethod === 'cash'
                  ? t('topup.cash_cta_hint')
                  : t('topup.qr_cta_hint')
                }
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
              {t('topup.confirm_payment')}
            </h3>

            {transactionData && (
              <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400 mb-6">
                <p><strong>{t('mifare.card_uid')}:</strong> {transactionData.cardUid}</p>
                <p><strong>{t('topup.amount')}:</strong> {transactionData.amount.toLocaleString()} LAK</p>
                <p><strong>{t('topup.bank')}:</strong> {transactionData.bank}</p>
                <p><strong>{t('topup.description')}:</strong> {transactionData.description}</p>
              </div>
            )}

            <p className="text-gray-600 dark:text-gray-400 text-sm mb-6">
              {t('topup.confirm_note_line1')}<br />
              <span className="text-blue-600 dark:text-blue-400 font-medium">{t('topup.confirm_note')}</span>
            </p>

            <div className="flex gap-3">
              <button
                onClick={confirmQRPayment}
                disabled={loading}
                className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors duration-200 ${loading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-green-500 hover:bg-green-600'
                  } text-white`}
              >
                {loading ? '🔄 กำลังยืนยัน...' : '✅ ยืนยัน'}
              </button>
              <button
                onClick={() => setShowConfirmModal(false)}
                disabled={loading}
                className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-2 px-4 rounded-lg transition-colors duration-200"
              >
                ยกเลิก
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Thermal Receipt Modal */}
      {showReceipt && receiptData && (
        <ThermalReceipt
          receiptData={receiptData}
          paperWidth={printWidth}
          autoPrint={receiptData?.autoPrint || false}
          onClose={() => {
            setShowReceipt(false);
            setReceiptData(null);
          }}
        />
      )}
    </div>
  );
}
